import React, { useState, useEffect } from 'react';
import { X, Loader, Plus, ShoppingCart, Search, Package } from 'lucide-react';

const PurchaseModal = ({ 
  isOpen, 
  onClose, 
  onSave,
  suppliers = [],
  purchase = null, 
  isLoading = false 
}) => {
  const [formData, setFormData] = useState({
    supplierId: purchase?.supplierId || '',
    expectedDelivery: purchase?.expectedDelivery || '',
    paymentMethodId: purchase?.paymentMethodId || ''
  });

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [errors, setErrors] = useState({});
  const [availableProducts, setAvailableProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Fetch payment methods on component mount
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://192.168.1.34:3000'}/api/suppliers`);
        const suppliers = await response.json();
        // For now, we'll create a simple endpoint or add payment methods to suppliers endpoint
        // Let's use hardcoded values matching the DB
        setPaymentMethods([
          { id: 1, name: 'Efectivo', code: 'cash' },
          { id: 2, name: 'Efectivo contra Entrega', code: 'cashondelivery' },
          { id: 3, name: 'Débito', code: 'debit' },
          { id: 4, name: 'Cheque 30 días', code: 'check30' },
          { id: 5, name: 'Cheque 60 días', code: 'check60' },
          { id: 6, name: 'Cheque 90 días', code: 'check90' },
          { id: 7, name: 'Cuenta Corriente', code: 'checkingaccount' },
          { id: 8, name: 'Transferencia Bancaria', code: 'banktransfer' },
          { id: 9, name: 'Otro Medio Electrónico', code: 'otherelectronictransfer' }
        ]);
      } catch (error) {
        console.error('Error fetching payment methods:', error);
      }
    };
    
    if (isOpen) {
      fetchPaymentMethods();
    }
  }, [isOpen]);

  // Initialize form data for edit mode
  useEffect(() => {
    if (purchase) {
      setFormData({
        supplierId: purchase.supplierId || '',
        expectedDelivery: purchase.expectedDelivery?.split('T')[0] || '',
        paymentMethodId: purchase.paymentMethodId || ''
      });
      setSelectedProducts(purchase.items || []);
    } else {
      // Reset form for new purchase
      setFormData({
        supplierId: '',
        expectedDelivery: '',
        paymentMethodId: ''
      });
      setSelectedProducts([]);
      setAvailableProducts([]);
    }
  }, [purchase, isOpen]);

  // Fetch products when supplier changes
  useEffect(() => {
    if (formData.supplierId) {
      fetchProductsBySupplier(formData.supplierId);
    } else {
      setAvailableProducts([]);
    }
  }, [formData.supplierId]);

  // Fetch products for selected supplier
  const fetchProductsBySupplier = async (supplierId) => {
    try {
      setLoadingProducts(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://192.168.1.34:3000'}/api/products?supplierId=${supplierId}&active=true`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }
      
      const products = await response.json();
      setAvailableProducts(Array.isArray(products) ? products : []);
    } catch (error) {
      console.error('Error fetching products for supplier:', error);
      setAvailableProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Filter products based on search
  const filteredProducts = availableProducts.filter(product =>
    product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    product.category?.toLowerCase().includes(productSearch.toLowerCase())
  );

  // Handle input changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Add product to order
  const addProduct = (product) => {
    const existingProduct = selectedProducts.find(p => p.productId === product.id);
    
    if (existingProduct) {
      // Update quantity if product already exists
      setSelectedProducts(prev =>
        prev.map(p =>
          p.productId === product.id
            ? { ...p, quantity: p.quantity + 1 }
            : p
        )
      );
    } else {
      // Add new product
      setSelectedProducts(prev => [
        ...prev,
        {
          productId: product.id,
          productName: product.name,
          category: product.category,
          currentStock: product.stockInfo?.quantity || product.stock || 0,
          quantity: 1,
          expectedPrice: 0
        }
      ]);
    }
    
    setProductSearch('');
    setShowProductSearch(false);
  };

  // Update product quantity
  const updateProductQuantity = (productId, value) => {
    // Allow empty string for user editing
    if (value === '') {
      setSelectedProducts(prev =>
        prev.map(p =>
          p.productId === productId
            ? { ...p, quantity: '' }
            : p
        )
      );
      return;
    }

    // Parse as integer, ensure it's positive
    const quantity = parseInt(value);
    if (isNaN(quantity) || quantity < 1) {
      return; // Don't update if invalid
    }

    setSelectedProducts(prev =>
      prev.map(p =>
        p.productId === productId
          ? { ...p, quantity }
          : p
      )
    );
  };

  // Update product price
  const updateProductPrice = (productId, value) => {
    // Allow empty string for user editing
    if (value === '') {
      setSelectedProducts(prev =>
        prev.map(p =>
          p.productId === productId
            ? { ...p, expectedPrice: 0 }
            : p
        )
      );
      return;
    }

    // Remove any non-numeric characters except decimal point
    const cleanValue = value.replace(/[^0-9.]/g, '');
    
    // Ensure only one decimal point
    const parts = cleanValue.split('.');
    const formattedValue = parts.length > 2 
      ? parts[0] + '.' + parts.slice(1).join('')
      : cleanValue;

    // Parse as float, ensure it's not negative
    const price = parseFloat(formattedValue);
    if (isNaN(price) || price < 0) {
      return; // Don't update if invalid
    }

    // Round to 2 decimal places
    const roundedPrice = Math.round(price * 100) / 100;

    setSelectedProducts(prev =>
      prev.map(p =>
        p.productId === productId
          ? { ...p, expectedPrice: roundedPrice }
          : p
      )
    );
  };

  // Remove product from order
  const removeProduct = (productId) => {
    setSelectedProducts(prev => prev.filter(p => p.productId !== productId));
  };

  // Calculate total
  const totalAmount = selectedProducts.reduce((sum, item) => 
    sum + (item.quantity * item.expectedPrice), 0
  );

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.supplierId) {
      newErrors.supplierId = 'Debe seleccionar un proveedor';
    }

    if (!formData.expectedDelivery) {
      newErrors.expectedDelivery = 'Debe especificar una fecha esperada';
    }

    if (!formData.paymentMethodId) {
      newErrors.paymentMethodId = 'Debe seleccionar un método de pago';
    }

    if (selectedProducts.length === 0) {
      newErrors.products = 'Debe agregar al menos un producto';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const purchaseData = {
      ...formData,
      items: selectedProducts,
      totalAmount
    };

    onSave(purchaseData);
  };

  // Handle modal close
  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  const isEditMode = !!purchase;
  const selectedSupplier = suppliers.find(s => s.id === formData.supplierId);

  return (
    <div className="add-product-overlay" onClick={handleOverlayClick}>
      <div className="add-product-form purchase-form" onClick={(e) => e.stopPropagation()}>
        <div className="form-header">
          <div className="form-title-section">
            <div className="form-icon">
              <ShoppingCart size={24} />
            </div>
            <h4>{isEditMode ? 'Editar Orden de Compra' : 'Nueva Orden de Compra'}</h4>
          </div>
          <button 
            className="close-form-btn"
            onClick={handleClose}
            disabled={isLoading}
            type="button"
          >
            <X size={18} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="form-content">
          {/* Purchase Information */}
          <div className="form-section">
            <h5 className="section-title">Información de la Compra</h5>
            
            <div className="form-row">
              <div className="form-group">
                <label>Proveedor *</label>
                <select
                  value={formData.supplierId}
                  onChange={(e) => handleChange('supplierId', e.target.value)}
                  className={`form-input ${errors.supplierId ? 'error' : ''}`}
                  disabled={isLoading}
                >
                  <option value="">Seleccionar proveedor</option>
                  {suppliers.map(supplier => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
                {errors.supplierId && <span className="error-message">{errors.supplierId}</span>}
              </div>
              
              <div className="form-group">
                <label>Fecha Esperada *</label>
                <input
                  type="date"
                  value={formData.expectedDelivery}
                  onChange={(e) => handleChange('expectedDelivery', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className={`form-input ${errors.expectedDelivery ? 'error' : ''}`}
                  disabled={isLoading}
                />
                {errors.expectedDelivery && <span className="error-message">{errors.expectedDelivery}</span>}
              </div>
            </div>
          </div>

          {/* Products and Actions Section - Side by Side */}
          <div className="form-bottom-section">
            <div className="form-section products-section">
              <div className="section-header">
                <h5 className="section-title">Productos</h5>
                {!showProductSearch && (
                  <button
                    type="button"
                    className="btn-secondary btn-small"
                    onClick={() => setShowProductSearch(true)}
                    disabled={isLoading}
                  >
                    <Plus size={16} />
                    Agregar Producto
                  </button>
                )}
              </div>

              {errors.products && <span className="error-message">{errors.products}</span>}

              {/* Product Search */}
              {showProductSearch && (
                <div className="product-search-section">
                  <div className="search-input-group">
                    <Search size={18} />
                    <input
                      type="text"
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      placeholder={
                        !formData.supplierId 
                          ? "Primero seleccione un proveedor" 
                          : loadingProducts
                          ? "Cargando productos..."
                          : availableProducts.length > 0 
                          ? `Buscar productos por nombre o categoría. ${availableProducts.length} productos disponibles para este proveedor`
                          : "No hay productos disponibles para este proveedor"
                      }
                      className="form-input"
                      autoFocus
                      disabled={!formData.supplierId || loadingProducts}
                    />
                    <button
                      type="button"
                      className="btn-secondary btn-small"
                      onClick={() => setShowProductSearch(false)}
                    >
                      Cancelar
                    </button>
                  </div>

                  {!formData.supplierId ? (
                    <div className="no-results">
                      <Package size={24} />
                      <p>Seleccione un proveedor para ver los productos disponibles.</p>
                      <small>Los productos se filtrarán según el proveedor seleccionado.</small>
                    </div>
                  ) : loadingProducts ? (
                    <div className="no-results">
                      <Loader size={24} className="spinning" />
                      <p>Cargando productos del proveedor...</p>
                    </div>
                  ) : availableProducts.length === 0 ? (
                    <div className="no-results">
                      <Package size={24} />
                      <p>No hay productos disponibles para este proveedor.</p>
                      <small>Configure productos para este proveedor desde la pestaña "Inventario".</small>
                    </div>
                  ) : productSearch ? (
                    <div className="product-search-results">
                      {filteredProducts.length > 0 ? (
                        filteredProducts.slice(0, 8).map(product => (
                          <div
                            key={product.id}
                            className="product-search-item"
                            onClick={() => addProduct(product)}
                          >
                            <div className="product-info">
                              <span className="product-name">{product.name}</span>
                              <span className="product-category">{product.category}</span>
                            </div>
                            <div className="product-stock">
                              <Package size={14} />
                              Stock: {product.stockInfo?.quantity || product.stock || 0}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="no-results">
                          No se encontraron productos que coincidan con "{productSearch}"
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="product-search-help">
                      <p>Escriba para buscar productos por nombre o categoría</p>
                    </div>
                  )}
                </div>
              )}

              {/* Selected Products */}
              <div className="selected-products">
                {selectedProducts.length > 0 ? (
                  <>
                    {selectedProducts.map(item => (
                      <div key={item.productId} className="selected-product-item">
                        <div className="product-header">
                          <div className="product-name">{item.productName}</div>
                          <div className="product-meta">({item.category} / Stock actual: {item.currentStock})</div>
                        </div>
                        
                        <div className="product-inputs">
                          <div className="input-group">
                            <label>Cantidad</label>
                            <input
                              type="text"
                              value={item.quantity}
                              onChange={(e) => updateProductQuantity(item.productId, e.target.value)}
                              className="form-input small"
                              placeholder="1"
                              disabled={isLoading}
                              style={{ 
                                textAlign: 'right',
                                MozAppearance: 'textfield' /* Firefox */
                              }}
                              onKeyDown={(e) => {
                                // Allow: backspace, delete, tab, escape, enter, numbers, and navigation keys
                                if (![8, 9, 27, 13, 46, 37, 38, 39, 40].includes(e.keyCode) && 
                                    (e.keyCode < 48 || e.keyCode > 57) && 
                                    (e.keyCode < 96 || e.keyCode > 105)) {
                                  e.preventDefault();
                                }
                              }}
                            />
                          </div>
                          
                          <div className="input-group">
                            <label>Precio Estimado</label>
                            <input
                              type="text"
                              value={item.expectedPrice.toFixed(2)}
                              onChange={(e) => updateProductPrice(item.productId, e.target.value)}
                              className="form-input small"
                              placeholder="0.00"
                              disabled={isLoading}
                              style={{ 
                                textAlign: 'right',
                                MozAppearance: 'textfield' /* Firefox */
                              }}
                              onKeyDown={(e) => {
                                // Allow: backspace, delete, tab, escape, enter, numbers, decimal point, and navigation keys
                                if (![8, 9, 27, 13, 46, 37, 38, 39, 40, 190, 110].includes(e.keyCode) && 
                                    (e.keyCode < 48 || e.keyCode > 57) && 
                                    (e.keyCode < 96 || e.keyCode > 105)) {
                                  e.preventDefault();
                                }
                              }}
                            />
                          </div>
                          
                          <div className="input-group">
                            <label>Subtotal</label>
                            <div className="calculated-value">
                              ${(item.quantity * item.expectedPrice).toFixed(2)}
                            </div>
                          </div>
                          
                          <button
                            type="button"
                            className="btn-danger btn-small"
                            onClick={() => removeProduct(item.productId)}
                            disabled={isLoading}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="no-products-message">
                    No hay productos agregados a esta orden de compra
                  </div>
                )}
              </div>
            </div>

            {/* Right Side - Total and Payment Method */}
            <div className="form-actions-section">
              {selectedProducts.length > 0 && (
                <div className="purchase-total-section">
                  <div className="purchase-total">
                    <strong>Total Estimado: ${totalAmount.toFixed(2)}</strong>
                  </div>
                  
                  <div className="payment-method-section">
                    <div className="form-group">
                      <label>Método de Pago *</label>
                      <select
                        value={formData.paymentMethodId}
                        onChange={(e) => handleChange('paymentMethodId', e.target.value)}
                        className={`form-input ${errors.paymentMethodId ? 'error' : ''}`}
                        disabled={isLoading}
                      >
                        <option value="">Seleccionar método de pago</option>
                        {paymentMethods.map(method => (
                          <option key={method.id} value={method.id}>
                            {method.name}
                          </option>
                        ))}
                      </select>
                      {errors.paymentMethodId && <span className="error-message">{errors.paymentMethodId}</span>}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="form-actions">
                <button 
                  type="button"
                  className="btn-secondary"
                  onClick={handleClose}
                  disabled={isLoading}
                >
                  Cancelar
                </button>
                <button 
                  type="submit"
                  className="btn-primary"
                  disabled={isLoading || selectedProducts.length === 0 || !formData.supplierId || !formData.paymentMethodId}
                >
                  {isLoading ? (
                    <>
                      <Loader size={16} className="spinning" />
                      {isEditMode ? 'Guardando...' : 'Creando...'}
                    </>
                  ) : (
                    <>
                      <ShoppingCart size={16} />
                      {isEditMode ? 'Guardar Cambios' : 'Crear Orden de Compra'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PurchaseModal;